import { useEffect, useRef, useState, type FC } from "react";
import "./index.scss";

import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import { useAppDispatch } from "../../app/hooks";
import { getUser } from "../../app/services/userService";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { useLocation, useNavigate } from "react-router-dom";
import { getOrderStatusesForMenu } from "../../app/services/orderStatusService";
import { Dropdown } from "react-bootstrap";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { setClientType } from "../../app/slices/clientTypeSlice";
import { Lock } from "react-bootstrap-icons";

interface Props {}

const Navigation: FC<Props> = () => {
  const orderStatuses = useSelector((state: RootState) => state.orderStatus);
  const dispatch = useAppDispatch();

  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const user = useSelector((state: RootState) => state.user);
  const displayName: string | null = user.displayName ?? "ERROR";

  const clientToggleLocked = useSelector(
    (state: RootState) => state.clientType.toggleLocked,
  );
  const clientType = useSelector(
    (state: RootState) => state.clientType.clientType,
  );
  const clientTypeLabel =
    clientType === ClientType.Standard
      ? "Skadeverkstad"
      : clientType === ClientType.Catalog
        ? "AD Butik"
        : "Fleet";

  const navigate = useNavigate();

  const navigateAdminHandler = () => {
    navigate("/admin", { replace: true });
  };
  const navigateSuppliersHandler = () => {
    navigate("/suppliers", { replace: true });
  };
  const navigateCustomersHandler = () => {
    navigate("/customers", { replace: true });
  };
  const navigateCarBrandsHandler = () => {
    navigate("/car-brands", { replace: true });
  };
  const navigateBrandDiscountsHandler = () => {
    navigate("/brand-discounts", { replace: true });
  };
  const navigateArticleGroupDiscountsHandler = () => {
    navigate("/article-group-discounts", { replace: true });
  };
  const navigateArticleDiscountsHandler = () => {
    navigate("/article-discounts", { replace: true });
  };  
  const clientTypeHandler = (type: ClientType) => {
    dispatch(setClientType(type));
  };
  const [showOverlay, setShowOverlay] = useState(false);

  const prevClientType = useRef<ClientType | undefined>(undefined);

  useEffect(() => {
    document.body.setAttribute("data-bs-theme", "standard");

    if (clientType === ClientType.Catalog) {
      document.body.setAttribute("data-bs-theme", "catalog");
    }
    if (clientType === ClientType.Fleet) {
      document.body.setAttribute("data-bs-theme", "fleet");
    }
  }, [clientType]);

  useEffect(() => {
    dispatch(getUser(instance, account));
  }, [account, accounts, dispatch, instance]);

  useEffect(() => {
    dispatch(getOrderStatusesForMenu(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    if (prevClientType.current === undefined) {
      prevClientType.current = clientType;
      return;
    }
    if (prevClientType.current !== clientType) {
      setShowOverlay(true);
      const timer = setTimeout(() => setShowOverlay(false), 2000);
      prevClientType.current = clientType;
      return () => clearTimeout(timer);
    }
  }, [clientType]);

  function signOffClickHandler() {
    instance.logout();
  }

  const location = useLocation().pathname;

  return (
    <>
      <div className={`toggle-overlay${showOverlay ? " fade-in" : ""}`}>
        <span className="toggle-overlay-message">
          Byter till {clientTypeLabel}
        </span>
      </div>
      <Navbar
        expand="lg"
        data-bs-theme={
          clientType === ClientType.Standard
            ? "standard"
            : clientType === ClientType.Catalog
              ? "catalog"
              : "fleet"
        }
      >
        <Container>
          <Navbar.Brand className="brand" href="/">
            <img width="40" height="40" src="/favicon.ico" alt="AD One"></img>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            {orderStatuses.orderStatusListForMenu.map((status) => (
              <Nav key={status.id}>
                <Nav.Link
                  className={
                    location.toLocaleLowerCase().includes(status.id)
                      ? "active-nav-item"
                      : ""
                  }
                  href={`/orderstatus/${status.id}`}
                >
                  {status.name}
                </Nav.Link>
              </Nav>
            ))}
            <Nav className="navbar-user">
              <div className="dropdown-wrapper">
                {!clientToggleLocked ? (
                  <Dropdown className="client-type-dropdown">
                    <Dropdown.Toggle
                      variant="primary"
                      id="dropdown-client-type"
                    >
                      {clientTypeLabel}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={() => clientTypeHandler(ClientType.Standard)}
                      >
                        Skadeverkstad
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => clientTypeHandler(ClientType.Catalog)}
                      >
                        AD Butik
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => clientTypeHandler(ClientType.Fleet)}
                      >
                        Fleet
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <div>
                    <Lock /> {clientTypeLabel}
                  </div>
                )}
              </div>
              <Nav.Link>
                <Dropdown className="nav-dropdown">
                  <Dropdown.Toggle variant="primary" id="dropdown-signout">
                    {displayName}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item href="/admin" onClick={navigateAdminHandler}>
                      Admin
                    </Dropdown.Item>
                    <Dropdown.Item
                      href="/suppliers"
                      onClick={navigateSuppliersHandler}
                    >
                      Leverantörer
                    </Dropdown.Item>
                    <Dropdown.Item
                      href="/customers"
                      onClick={navigateCustomersHandler}
                    >
                      Kunder
                    </Dropdown.Item>
                    <Dropdown.Item
                      href="/car-brand"
                      onClick={navigateCarBrandsHandler}
                    >
                      Bilmärken
                    </Dropdown.Item>
                    <Dropdown.Item
                      href="/brand-discounts"
                      onClick={navigateBrandDiscountsHandler}
                    >
                      Bilmärkes-rabatt
                    </Dropdown.Item>
                    <Dropdown.Item
                      href="/article-group-discounts"
                      onClick={navigateArticleGroupDiscountsHandler}
                    >
                      Artikelgrupp-rabatt
                    </Dropdown.Item>
                    <Dropdown.Item
                      href="/article-discounts"
                      onClick={navigateArticleDiscountsHandler}
                    >
                      Artikel-rabatt
                    </Dropdown.Item>
                    <Dropdown.Item href="/" onClick={signOffClickHandler}>
                      Logga ut
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </>
  );
};

export default Navigation;
